import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Brain, Cpu, Database, Zap, Shield, TrendingUp, Activity, Layers } from 'lucide-react';
import SEOHead from '../../../components/SEO/SEOHead';
import SEOFooter from '../../../components/SEO/SEOFooter';

const TecnologiaIA = () => {
  return (
    <>
      <SEOHead 
        customTitle="Tecnologia de IA - Como Nossa IA de Apostas Esportivas Funciona | PickStar AI"
        customDescription="Descubra a tecnologia de IA de ponta por trás das previsões esportivas do PickStar. Machine learning, redes neurais e análise de dados em tempo real explicados."
      />
      
      <div className="min-h-screen bg-black">
        {/* Header */}
        <header className="bg-white/[0.02] backdrop-blur-sm border-b border-white/[0.05] sticky top-0 z-50">
          <div className="max-w-screen-xl mx-auto px-6 py-4">
            <Link to="/" className="inline-flex items-center gap-2 text-[11px] text-white/40 hover:text-emerald-400/70 uppercase tracking-[0.2em] transition-colors duration-200">
              <ArrowLeft className="w-3 h-3" />
              Voltar ao Início
            </Link>
          </div>
        </header>

        {/* Hero Section */}
        <section className="relative py-20 px-6 bg-gradient-to-b from-white/[0.02] to-transparent">
          <div className="absolute inset-0 backdrop-blur-sm" />
          <div className="relative max-w-screen-xl mx-auto">
            <div className="mb-8 pb-6 border-b border-white/[0.05]">
              <h1 className="text-xs font-medium tracking-[0.3em] text-white/30 uppercase mb-3">
                INTELIGÊNCIA ARTIFICIAL
              </h1>
              <h2 className="text-4xl md:text-5xl font-bold text-white/95 mb-6">
                Tecnologia de IA
              </h2>
              <p className="text-sm text-white/70 max-w-2xl">
                A ciência de ponta que impulsiona previsões esportivas líderes do setor
              </p>
            </div>
            {/* Live Status Indicator */}
            <div className="absolute top-8 right-8">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-emerald-500/70 rounded-full animate-pulse" />
                <span className="text-[10px] text-emerald-400/80 uppercase tracking-wider">SYSTEM ONLINE</span>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <main className="px-6 pb-20">
          <div className="max-w-screen-xl mx-auto space-y-8">
            
            {/* Introduction */}
            <section className="relative bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg p-8 hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200 group">
              <h2 className="text-xs font-medium tracking-[0.2em] text-white/30 uppercase mb-3">
                NOVA GERAÇÃO
              </h2>
              <h3 className="text-xl font-bold text-white/95 mb-6">
                Inteligência Esportiva de Nova Geração
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <p className="text-sm text-white/50 leading-relaxed">
                  O PickStar AI representa um avanço na tecnologia de apostas esportivas. Nosso sistema proprietário
                  combina múltiplas tecnologias de IA para entregar previsões que consistentemente superam métodos
                  tradicionais de apostas.
                </p>
                <p className="text-sm text-white/50 leading-relaxed">
                  Construído com anos de pesquisa e milhões de jogos históricos, nossa IA não apenas prevê
                  resultados—ela entende as dinâmicas complexas que determinam resultados esportivos.
                </p>
              </div>
            </section>

            {/* Core Technologies */}
            <section className="relative bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg p-8">
              <h2 className="text-xs font-medium tracking-[0.2em] text-white/30 uppercase mb-3">
                CORE TECH
              </h2>
              <h3 className="text-2xl font-bold text-white/95 mb-8">
                Tecnologias Fundamentais
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-md p-6 hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200 group">
                  <div className="mb-4">
                    <span className="text-[10px] text-white/30 uppercase tracking-wider">NEURAL NETWORKS</span>
                    <h3 className="text-sm text-white/70 mt-2">Redes Neurais</h3>
                  </div>
                  <p className="text-xs text-white/50 mb-4 leading-relaxed">
                    Modelos de deep learning com milhões de parâmetros treinados em dados esportivos históricos
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-[11px] text-white/30 hover:text-white/50 transition-colors">
                      <span className="text-emerald-400/40 mt-0.5">→</span>
                      <span>Arquiteturas Transformer para previsão</span>
                    </li>
                    <li className="flex items-start gap-2 text-[11px] text-white/30 hover:text-white/50 transition-colors">
                      <span className="text-emerald-400/40 mt-0.5">→</span>
                      <span>Redes convolucionais para padrões</span>
                    </li>
                    <li className="flex items-start gap-2 text-[11px] text-white/30 hover:text-white/50 transition-colors">
                      <span className="text-emerald-400/40 mt-0.5">→</span>
                      <span>Redes recorrentes para séries temporais</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-md p-6 hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200 group">
                  <div className="mb-4">
                    <span className="text-[10px] text-white/30 uppercase tracking-wider">BIG DATA</span>
                    <h3 className="text-sm text-white/70 mt-2">Processamento de Big Data</h3>
                  </div>
                  <p className="text-xs text-white/50 mb-4 leading-relaxed">
                    Análise em tempo real de milhões de pontos de dados por segundo
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-[11px] text-white/30 hover:text-white/50 transition-colors">
                      <span className="text-emerald-400/40 mt-0.5">→</span>
                      <span>20+ anos de dados históricos</span>
                    </li>
                    <li className="flex items-start gap-2 text-[11px] text-white/30 hover:text-white/50 transition-colors">
                      <span className="text-emerald-400/40 mt-0.5">→</span>
                      <span>50+ casas de apostas ao vivo</span>
                    </li>
                    <li className="flex items-start gap-2 text-[11px] text-white/30 hover:text-white/50 transition-colors">
                      <span className="text-emerald-400/40 mt-0.5">→</span>
                      <span>Análise de sentimento social</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-md p-6 hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200 group">
                  <div className="mb-4">
                    <span className="text-[10px] text-white/30 uppercase tracking-wider">PREDICTIVE</span>
                    <h3 className="text-sm text-white/70 mt-2">Análise Preditiva</h3>
                  </div>
                  <p className="text-xs text-white/50 mb-4 leading-relaxed">
                    Modelos estatísticos avançados para probabilidade de resultados
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-[11px] text-white/30 hover:text-white/50 transition-colors">
                      <span className="text-emerald-400/40 mt-0.5">→</span>
                      <span>Inferência Bayesiana</span>
                    </li>
                    <li className="flex items-start gap-2 text-[11px] text-white/30 hover:text-white/50 transition-colors">
                      <span className="text-emerald-400/40 mt-0.5">→</span>
                      <span>Simulações Monte Carlo</span>
                    </li>
                    <li className="flex items-start gap-2 text-[11px] text-white/30 hover:text-white/50 transition-colors">
                      <span className="text-emerald-400/40 mt-0.5">→</span>
                      <span>Métodos ensemble</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-md p-6 hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200 group">
                  <div className="mb-4">
                    <span className="text-[10px] text-white/30 uppercase tracking-wider">NLP</span>
                    <h3 className="text-sm text-white/70 mt-2">Processamento de NLP</h3>
                  </div>
                  <p className="text-xs text-white/50 mb-4 leading-relaxed">
                    Compreensão de contexto de notícias e relatórios
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-[11px] text-white/30 hover:text-white/50 transition-colors">
                      <span className="text-emerald-400/40 mt-0.5">→</span>
                      <span>Análise de lesões</span>
                    </li>
                    <li className="flex items-start gap-2 text-[11px] text-white/30 hover:text-white/50 transition-colors">
                      <span className="text-emerald-400/40 mt-0.5">→</span>
                      <span>Extração de notícias</span>
                    </li>
                    <li className="flex items-start gap-2 text-[11px] text-white/30 hover:text-white/50 transition-colors">
                      <span className="text-emerald-400/40 mt-0.5">→</span>
                      <span>Declarações de técnicos</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* How It Works */}
            <section className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10">
              <div className="flex items-center gap-3 mb-6">
                <Zap className="w-6 h-6 text-emerald-400" />
                <h2 className="text-2xl font-bold text-white">Como Nossa IA Funciona</h2>
              </div>
              
              <div className="space-y-6">
                <div className="relative">
                  <div className="absolute left-5 top-12 bottom-0 w-0.5 bg-emerald-500/30"></div>
                  
                  <div className="space-y-8">
                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">1</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-white mb-2">Coleta de Dados</h3>
                        <p className="text-gray-300 text-sm">
                          Nosso sistema coleta continuamente dados de múltiplas fontes: estatísticas de jogos, 
                          métricas de performance de jogadores, condições climáticas, relatórios de lesões, movimentos de odds 
                          e dados históricos de confrontos.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">2</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-white mb-2">Engenharia de Características</h3>
                        <p className="text-gray-300 text-sm">
                          Dados brutos são transformados em características significativas: índices de forma da equipe, 
                          performance head-to-head, estatísticas situacionais e indicadores de momentum 
                          que nossos modelos podem processar efetivamente.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">3</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-white mb-2">Previsão do Modelo</h3>
                        <p className="text-gray-300 text-sm">
                          Múltiplos modelos de IA especializados analisam os dados simultaneamente. Cada modelo 
                          foca em aspectos diferentes: padrões de pontuação, métricas defensivas, times especiais 
                          e vantagens situacionais.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">4</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-white mb-2">Consenso Ensemble</h3>
                        <p className="text-gray-300 text-sm">
                          Nosso sistema ensemble combina previsões de todos os modelos, pesando-os baseado 
                          na precisão histórica e níveis atuais de confiança para produzir uma previsão final 
                          com pontuações de probabilidade.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">5</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-white mb-2">Insights Legíveis</h3>
                        <p className="text-gray-300 text-sm">
                          Nosso sistema de geração de linguagem natural traduz previsões complexas em 
                          insights claros e acionáveis explicando o raciocínio por trás de cada escolha.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Performance Metrics */}
            <section className="bg-gradient-to-r from-emerald-500/[0.02] to-violet-500/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg p-8">
              <h2 className="text-xs font-medium tracking-[0.2em] text-white/30 uppercase mb-8">
                MÉTRICAS DE PERFORMANCE
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-md p-6 text-center hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200 group">
                  <div className="font-mono text-2xl text-emerald-400/80 group-hover:text-emerald-400/90 mb-2">ELITE</div>
                  <div className="text-[10px] text-white/30 uppercase tracking-wider">Desempenho</div>
                </div>
                <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-md p-6 text-center hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200 group">
                  <div className="font-mono text-2xl text-emerald-400/80 group-hover:text-emerald-400/90 mb-2">2.8M</div>
                  <div className="text-[10px] text-white/30 uppercase tracking-wider">Jogos Analisados</div>
                </div>
                <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-md p-6 text-center hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200 group">
                  <div className="font-mono text-2xl text-violet-400/70 group-hover:text-violet-400/90 mb-2">0.3s</div>
                  <div className="text-[10px] text-white/30 uppercase tracking-wider">Tempo de Resposta</div>
                </div>
                <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-md p-6 text-center hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200 group">
                  <div className="font-mono text-2xl text-violet-400/70 group-hover:text-violet-400/90 mb-2">24/7</div>
                  <div className="text-[10px] text-white/30 uppercase tracking-wider">Disponibilidade</div>
                </div>
              </div>

              <p className="text-xs text-white/50 leading-relaxed">
                Nosso sistema de IA foi testado em mais de 10 anos de dados históricos e melhora continuamente
                através de machine learning. A performance é verificada independentemente e atualizada diariamente.
              </p>
            </section>

            {/* Continuous Learning */}
            <section className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="w-6 h-6 text-emerald-400" />
                <h2 className="text-2xl font-bold text-white">Aprendizado Contínuo e Melhoria</h2>
              </div>
              
              <div className="space-y-4">
                <p className="text-gray-300">
                  Nossa IA não apenas faz previsões—ela aprende com cada resultado para se tornar mais precisa ao longo do tempo:
                </p>
                
                <div className="space-y-4">
                  <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-md p-4 hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200">
                    <h3 className="text-sm font-medium text-white/80 mb-2">Atualizações Diárias do Modelo</h3>
                    <p className="text-xs text-white/50 leading-relaxed">
                      Modelos são retreinados todas as noites com os últimos resultados de jogos e ajustados para padrões emergentes
                    </p>
                  </div>

                  <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-md p-4 hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200">
                    <h3 className="text-sm font-medium text-white/80 mb-2">Algoritmos Adaptativos</h3>
                    <p className="text-xs text-white/50 leading-relaxed">
                      Nosso sistema se ajusta automaticamente a mudanças de regras, atualizações de elenco e mudanças meta nos esportes
                    </p>
                  </div>

                  <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-md p-4 hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200">
                    <h3 className="text-sm font-medium text-white/80 mb-2">Análise de Erros</h3>
                    <p className="text-xs text-white/50 leading-relaxed">
                      Cada previsão incorreta é analisada para identificar padrões e melhorar a precisão futura
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Why Choose Our AI */}
            <section className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-6">Por que o PickStar AI Lidera a Indústria</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-400 mt-2"></div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Análise Imparcial</h3>
                      <p className="text-gray-300 text-sm">
                        Sem apego emocional ou viés humano—previsões puramente baseadas em dados
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-400 mt-2"></div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Processamento em Tempo Real</h3>
                      <p className="text-gray-300 text-sm">
                        Análise instantânea de notícias de última hora e mudanças de escalação
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-400 mt-2"></div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Cobertura Abrangente</h3>
                      <p className="text-gray-300 text-sm">
                        Análise para todos os principais esportes e ligas mundiais
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-violet-400 mt-2"></div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Raciocínio Transparente</h3>
                      <p className="text-gray-300 text-sm">
                        Explicações claras para cada previsão e nível de confiança
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-violet-400 mt-2"></div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Histórico Comprovado</h3>
                      <p className="text-gray-300 text-sm">
                        Taxa de sucesso líder do setor verificada em milhões de previsões
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-violet-400 mt-2"></div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Evolução Constante</h3>
                      <p className="text-gray-300 text-sm">
                        IA que aprende e melhora com cada jogo
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section className="text-center py-12">
              <h2 className="text-xs font-medium tracking-[0.3em] text-white/30 uppercase mb-4">
                EXPERIMENTE O FUTURO DAS APOSTAS
              </h2>
              <p className="text-sm text-white/50 mb-8">
                Veja nossa tecnologia de IA em ação com sua primeira previsão
              </p>
              <Link
                to="/chat"
                className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-emerald-500/80 to-violet-500/80 backdrop-blur-sm rounded-md text-white text-[11px] font-medium tracking-[0.15em] uppercase hover:from-emerald-500 hover:to-violet-500 transition-all duration-200 shadow-lg shadow-emerald-500/10"
              >
                EXPERIMENTE PREVISÕES DE IA AGORA
              </Link>
            </section>
          </div>
        </main>

        {/* Footer */}
        <SEOFooter language="pt-BR" />
      </div>
    </>
  );
};

export default TecnologiaIA;